import { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import * as yup from 'yup';
import { TFunction, useTranslation } from '@packlink/translation-provider';
import { BrandedEmailsSettingsRepository, BrandedEmailTemplateParams, EmailType } from '@packlink/packlink-sdk';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { useToast } from '@shipengine/giger';
import { BrandedEmailEditionContent } from './BrandedEmailEditionContent';
import { useBrandedEmailImplementation } from '@common/hooks/useBrandedEmailImplementation';

export type BrandedEmailEditionProps = {
    emailType: EmailType;
};
export interface LanguageTabOptions {
    label: string;
    value: string;
}

export function BrandedEmailEdition({ emailType }: BrandedEmailEditionProps): JSX.Element {
    const { t } = useTranslation();
    const { apiClient } = useSdk();

    const [selectedLanguage, setSelectedLanguage] = useState<LanguageTabOptions>();

    const { brandedEmailImplementation } = useBrandedEmailImplementation(emailType, selectedLanguage?.value);
    const toast = useToast(t);
    const validationSchema = getTemplateValidationSchema(t);

    const handleSuccess = () => {
        toast.success({
            title: t('branded-emails.form.submit-success-title'),
            message: t('branded-emails.form.submit-success'),
        });
    };

    const onSubmit = (values: BrandedEmailTemplateParams, formikHelpers: FormikHelpers<BrandedEmailTemplateParams>) => {
        const brandedEmailsSettingsRepository = new BrandedEmailsSettingsRepository(apiClient);

        if (!brandedEmailImplementation?.locale) {
            return;
        }

        return brandedEmailsSettingsRepository
            .updateBrandedEmailImplementation(emailType, brandedEmailImplementation?.locale, values)
            .then(() => {
                formikHelpers.setTouched({});
                handleSuccess?.();
            })
            .catch(() => {
                toast.error({
                    message: t('branded-emails.error.try-again'),
                });
            });
    };

    return (
        <Formik<BrandedEmailTemplateParams>
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            initialValues={{
                subject: brandedEmailImplementation?.subject ?? '',
                body: brandedEmailImplementation?.body,
            }}
            validateOnBlur
            validateOnChange
            enableReinitialize
        >
            <BrandedEmailEditionContent
                emailType={emailType}
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
            />
        </Formik>
    );
}

export function getTemplateValidationSchema(t: TFunction) {
    return yup.object<BrandedEmailTemplateParams>({
        subject: yup.string().required(t('form.error.required', { field: t('form.label.branded-email-subject') })),
        body: yup.string().optional(),
    });
}
